import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { FunctionComponent, ReactNode } from 'react';
import { useMemo } from 'react';
import { useLocation } from 'react-router';

import { useTranslate } from '~/shared/context/use-translate';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { Button } from '~/ui/core/button/Button';
import { EmptyStateMessage } from '~/ui/core/empty-state-message/EmptyStateMessage';
import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';

import type { AiTransformationItem } from '../../../data-contract/ai-transformation-strategy';
import type { AiTransformationFilters } from '../../../fetchers/position-management/ai-transformation-fetchers';
import {
    createWorkTaskTilePayload,
    isWorkTaskTilePayload,
    type JobProfileTileSubject,
} from '../../../job-architecture-v2';
import {
    createPositionTilePayload,
    PositionAutomationTasks,
    selectPositionIdFromTile,
    useEnabledPositionBootstrap,
} from '../../../position-management';
import { useUrlTiles } from '../../../url-tiles';
import { QueryError } from '../../components/QueryError';
import { type StrategyGroupGrouping, useStrategyGroupItems } from '../api/use-strategy-group-items';
import { scoreLevelLabelKey } from '../model/score-levels';
import { statusLabelKey, statusPresentation } from '../model/statuses';
import { DEFAULT_STRATEGY_VIEW_STATE, parseStrategyViewState, toQueryFiltersWithoutPick } from '../model/view-state';
import { roleTaskAutomation } from './role-task-automation';

const NO_FILTERS = toQueryFiltersWithoutPick(DEFAULT_STRATEGY_VIEW_STATE);

export type RoleTilePositionScope =
    | { readonly status: 'none' | 'pending' }
    | {
          readonly architectureId: string;
          readonly filters: AiTransformationFilters;
          readonly grouping: StrategyGroupGrouping;
          readonly groupId: string;
          readonly status: 'ready';
      };

/**
 * Where the tile reads its positions and tasks: the Position Architecture it was opened for, under the filters of the
 * page that opened it, or else the one connected to its Job Architecture, unfiltered.
 */
export const useRoleTilePositionScope = ({
    architectureId,
    itemId,
    itemType,
    positionArchitectureId,
}: JobProfileTileSubject): RoleTilePositionScope => {
    const { search } = useLocation();
    const { bootstrap, isPending } = useEnabledPositionBootstrap();
    const grouping: StrategyGroupGrouping = itemType === 'job-role' ? 'ROLE' : 'LEVEL';
    const pageFilters = useMemo(
        () => toQueryFiltersWithoutPick(parseStrategyViewState(new URLSearchParams(search))),
        [search],
    );

    if (is.string(positionArchitectureId)) {
        return {
            architectureId: positionArchitectureId,
            filters: pageFilters,
            groupId: itemId,
            grouping,
            status: 'ready',
        };
    }
    if (isPending) return { status: 'pending' };

    const connected = bootstrap?.architectures.find(
        ({ connected_job_architecture_id }) => connected_job_architecture_id === architectureId,
    );

    return connected === undefined
        ? { status: 'none' }
        : { architectureId: connected.id, filters: NO_FILTERS, groupId: itemId, grouping, status: 'ready' };
};

type GroupItemsQuery = ReturnType<typeof useStrategyGroupItems>;

/** A position or task of the role or level: its title and potential, then its status and where it sits. */
const ItemRow: FunctionComponent<{
    readonly isOpen: boolean;
    readonly item: AiTransformationItem;
    readonly onOpen: () => void;
    readonly testId: string;
}> = ({ isOpen, item, onOpen, testId }) => {
    const translate = useTranslate();
    const { shortLabelKey } = statusPresentation(item.status);
    const context =
        item.subject === 'TASK'
            ? item.areas.map(({ title }) => title)
            : [item.area?.title, item.holderName].filter(is.string);

    const body = (
        <>
            <span className="flex w-full min-w-0 items-baseline justify-between gap-s">
                <span className="truncate text-body-s font-medium text-text-primary">{item.title}</span>
                <span className="shrink-0 text-body-xs text-text-secondary">
                    {translate(scoreLevelLabelKey(item.scoreLevel))}
                </span>
            </span>
            <span className="flex w-full min-w-0 items-center gap-s text-body-xs text-text-secondary">
                <span className="shrink-0" title={translate(statusLabelKey(item.status, item.subject))}>
                    {translate(shortLabelKey)}
                </span>
                {context.length === 0 ? null : <span className="truncate">{context.join(' · ')}</span>}
            </span>
        </>
    );
    const className =
        'm-0 flex w-full flex-col items-stretch gap-xxs rounded-lg border-none bg-transparent px-m py-s text-left font-sans';

    return (
        <li className="list-none">
            {item.canOpen ? (
                <Button
                    aria-current={isOpen}
                    className={clsx(className, 'hover:bg-surface-hover', isOpen && 'bg-surface-hover')}
                    data-item-id={item.id}
                    data-testid={testId}
                    type="button"
                    variant="plain"
                    onClick={onOpen}
                >
                    {body}
                </Button>
            ) : (
                <div className={className} data-item-id={item.id} data-testid={testId}>
                    {body}
                </div>
            )}
        </li>
    );
};

const ItemList: FunctionComponent<{
    readonly items: readonly AiTransformationItem[];
    readonly onOpen: (item: AiTransformationItem) => void;
    readonly openId: string | undefined;
    readonly testId: string;
}> = ({ items, onOpen, openId, testId }) => (
    <ul className="m-0 flex flex-col divide-y divide-border-soft p-0">
        {items.map((item) => (
            <ItemRow
                key={item.id}
                isOpen={item.id === openId}
                item={item}
                testId={testId}
                onOpen={() => onOpen(item)}
            />
        ))}
    </ul>
);

export const ItemsSkeleton: FunctionComponent<{ readonly label: string }> = ({ label }) => (
    <div aria-busy={true} aria-label={label} className="flex flex-col gap-s" role="status">
        {['first', 'second', 'third', 'fourth'].map((row) => (
            <div key={row} className="flex flex-col gap-xs px-m py-s">
                <SkeletonBase className="h-[1rem] w-3/4" />
                <SkeletonBase className="h-[0.75rem] w-2/5" />
            </div>
        ))}
    </div>
);

/** The items of a paged read, or its loading, error or empty state, with a button for the next page. */
const GroupItems: FunctionComponent<{
    readonly emptyKey: TranslationKey;
    readonly errorKey: TranslationKey;
    readonly loadingKey: TranslationKey;
    readonly query: GroupItemsQuery;
    readonly renderItems: (items: readonly AiTransformationItem[]) => ReactNode;
}> = ({ emptyKey, errorKey, loadingKey, query, renderItems }) => {
    const translate = useTranslate();
    const items = useMemo(() => query.data?.pages.flatMap((page) => page.items) ?? [], [query.data]);

    if (query.isError) {
        return <QueryError isRetrying={query.isFetching} message={errorKey} onRetry={() => void query.refetch()} />;
    }
    if (query.data === undefined) return <ItemsSkeleton label={translate(loadingKey)} />;
    if (items.length === 0) return <EmptyStateMessage>{translate(emptyKey)}</EmptyStateMessage>;

    return (
        <div className="flex flex-col gap-m">
            {renderItems(items)}
            {query.hasNextPage ? (
                <Button
                    className="self-center"
                    disabled={query.isFetching}
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                        if (!query.isFetching) void query.fetchNextPage();
                    }}
                >
                    {translate('common.show-more')}
                </Button>
            ) : null}
        </div>
    );
};

/** Items under a heading per level, in the order their levels first come. */
export const byLevel = (items: readonly AiTransformationItem[]) => {
    const groups = new Map<string, { readonly title: string; readonly items: AiTransformationItem[] }>();
    for (const item of items) {
        const key = item.level?.id ?? '';
        const group = groups.get(key) ?? { items: [], title: item.level?.title ?? '' };
        group.items.push(item);
        groups.set(key, group);
    }
    return [...groups.entries()];
};

export type ReadyScope = Extract<RoleTilePositionScope, { readonly status: 'ready' }>;

export const TaskItems: FunctionComponent<{ readonly scope: ReadyScope }> = ({ scope }) => {
    const { openTile, tiles } = useUrlTiles();
    const query = useStrategyGroupItems({ ...scope, subject: 'TASK' });
    const openTaskId = tiles
        .map(({ payload }) => (isWorkTaskTilePayload(payload) ? payload.taskId : undefined))
        .find(is.string);

    return (
        <GroupItems
            emptyKey="pages.ai-transformation.strategy.group-tile.tasks-empty"
            errorKey="pages.ai-transformation.strategy.group-tile.tasks-error"
            loadingKey="pages.ai-transformation.strategy.group-tile.loading"
            query={query}
            renderItems={(items) => (
                <PositionAutomationTasks
                    automation={roleTaskAutomation(items)}
                    openTaskId={openTaskId}
                    showHours={false}
                    onOpenTask={({ id, version }) => openTile(createWorkTaskTilePayload({ taskId: id, version }))}
                />
            )}
        />
    );
};

export const PositionItems: FunctionComponent<{ readonly scope: ReadyScope }> = ({ scope }) => {
    const { openTile, tiles } = useUrlTiles();
    const query = useStrategyGroupItems(scope);
    const openPositionId = tiles.map(({ payload }) => selectPositionIdFromTile(payload)).find(is.string);
    const open = (item: AiTransformationItem): void => openTile(createPositionTilePayload({ positionId: item.id }));

    return (
        <GroupItems
            emptyKey="pages.ai-transformation.strategy.group-tile.empty"
            errorKey="pages.ai-transformation.strategy.group-tile.error"
            loadingKey="pages.ai-transformation.strategy.group-tile.loading"
            query={query}
            renderItems={(items) =>
                scope.grouping === 'ROLE' ? (
                    byLevel(items).map(([levelId, group]) => (
                        <section key={levelId} className="flex min-w-0 flex-col gap-xxs">
                            {group.title === '' ? null : (
                                <h3 className="m-0 px-m text-body-xs font-medium text-text-secondary">
                                    {`${group.title} · ${group.items.length}`}
                                </h3>
                            )}
                            <ItemList
                                items={group.items}
                                openId={openPositionId}
                                testId="role-tile-position-row"
                                onOpen={open}
                            />
                        </section>
                    ))
                ) : (
                    <ItemList items={items} openId={openPositionId} testId="role-tile-position-row" onOpen={open} />
                )
            }
        />
    );
};

/** The Tasks tab: the tasks the role's or level's positions hold, or the profile's own tasks without positions. */
export const RoleTileTasks: FunctionComponent<JobProfileTileSubject & { readonly fallback: ReactNode }> = ({
    fallback,
    ...subject
}) => {
    const translate = useTranslate();
    const scope = useRoleTilePositionScope(subject);

    if (scope.status === 'pending') {
        return <ItemsSkeleton label={translate('pages.ai-transformation.strategy.group-tile.loading')} />;
    }

    return scope.status === 'ready' ? <TaskItems scope={scope} /> : fallback;
};

/** The Positions tab: the role's or level's positions, a role's under a heading per level. */
export const RoleTilePositions: FunctionComponent<JobProfileTileSubject> = (subject) => {
    const translate = useTranslate();
    const scope = useRoleTilePositionScope(subject);

    if (scope.status === 'pending') {
        return <ItemsSkeleton label={translate('pages.ai-transformation.strategy.group-tile.loading')} />;
    }

    return scope.status === 'ready' ? (
        <PositionItems scope={scope} />
    ) : (
        <EmptyStateMessage>{translate('pages.company-home.journey.headline.no-positions')}</EmptyStateMessage>
    );
};
